import React, { FC } from 'react';
import {
  DynamicComponent,
  Errors,
  HeaderText,
  BreadcrumbTree,
} from '@/components';
import { TypeAccountPage } from '@/core/generated-types';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  ErrorDirectiveProvider,
  useErrorDirectives,
} from '@/context/ErrorDirectiveContext';
import { ERROR_KINDS } from '@/core/constants';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';

export type CoursePackStreamingPageLayoutProps = {
  page: TypeAccountPage;
  error?: string | undefined;
};

type CoursePackStreamingPageChildrenProps = {
  page: TypeAccountPage;
  error?: string | undefined;
};

const CoursePackStreamingPageChildren: FC<
  CoursePackStreamingPageChildrenProps
> = ({ page, error }) => {
  const {
    fields: { components },
  } = page;

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_UNAVAILABLE,
  );
  if (error) {
    const errorsWithServerError = errors.concat(error);
    return <Errors errors={errorsWithServerError} heading={heading} />;
  }

  return (
    <section>
      {components &&
        components.map((component) => (
          <DynamicComponent key={component.sys.id} component={component} />
        ))}
    </section>
  );
};

const CoursePackStreamingPageLayout: FC<CoursePackStreamingPageLayoutProps> = ({
  page,
  error,
}) => {
  const {
    fields: { title, breadcrumbTree },
  } = page;

  return (
    <>
      {breadcrumbTree && (
        <BreadcrumbTree {...breadcrumbTree.fields} classes="ml-4" />
      )}
      <HeaderText
        text={title}
        align="center"
        size={HeaderSize.H1}
        classes="mt-6 mb-3 lg:mt-10 lg:mb-8 container mx-auto"
      />
      <ErrorDirectiveProvider errorDirectives={page.fields.errorDirectives}>
        <CoursePackStreamingPageChildren page={page} error={error} />
      </ErrorDirectiveProvider>
    </>
  );
};

export default React.memo(CoursePackStreamingPageLayout);
